import { AiChatToggle } from "@/features/ai-chat/AiChatToggle";
import { useSidebarStore } from "@/stores/useSidebarStore";
import { Fragment, type Key, useState } from "react";
import { useTheme } from "next-themes";
import useAccountInfo from "@/hooks/useAccountInfo";
import { api } from "@/lib/api-client";
import { sentryLog } from "@/utils/sentry-utils";
import {
  clearOriginalTokenForInfiltration,
  getOriginalTokenForInfiltration,
} from "@/utils/infiltrationSession";
import { useLogout } from "@/hooks/useLogout";
import { isMfaReauthTriggeredError } from "@/utils/init";
import { cn } from "@/utils";
import { useConsent } from "@/context/ConsentContext";
import { Avatar } from "@heroui/react";
import {
  Button,
  ButtonGroup,
  Popover,
  Separator,
  ToggleButton,
  ToggleButtonGroup,
} from "@heroui-v3/react";
import {
  AlertTriangle,
  BookOpenCheck,
  Cookie,
  Drama,
  ExternalLink,
  Headset,
  KeyRound,
  LogOut,
  Mail,
  Monitor,
  Moon,
  Phone,
  ShieldCheck,
  ShieldOff,
  Sun,
} from "lucide-react";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { KnowledgeBaseModal } from "./KnowledgeBaseModal";
import { CopyButton } from "../NextBase/CopyButton";
import { MfaResetModal } from "../MfaSetup/MfaResetModal";
import { PasskeysModal } from "@/Components/Passkeys/PasskeysModal";

export const UserPopover = () => {
  const sidebarOpen = useSidebarStore((state) => state.isOpen);

  const [isKnowledgeBaseModalOpen, setIsKnowledgeBaseModalOpen] =
    useState(false);
  const [isUserPopoverOpen, setIsUserPopoverOpen] = useState(false);
  const [isMfaResetModalOpen, setIsMfaResetModalOpen] = useState(false);
  const [isPasskeysModalOpen, setIsPasskeysModalOpen] = useState(false);

  const { openPreferences } = useConsent();

  const accountInfo = useAccountInfo((state) => state.accountInfo);

  const isAdminUser = ["admin", "salesman"].includes(accountInfo.systemRole);
  const {
    data: mfaStatus,
    error: mfaStatusError,
    isError: isMfaStatusError,
    isLoading: isMfaStatusLoading,
    refetch: refetchMfaStatus,
  } = api.mfa.useMfaStatus(isAdminUser);

  const { mutateAsync: loginAsAdmin } = api.admin.useLoginAsAdmin();

  const { mutateAsync: deleteCookie } = api.cookie.useDeleteHttpCookie();

  const { logout: logoutUser, closeImpersonationSession } = useLogout();

  const logout = () => logoutUser("sidebar");

  const handleLoginAsAdmin = async () => {
    setIsUserPopoverOpen(false);

    try {
      // Must run before the token swap — the impersonated token is what
      // identifies the session being closed.
      await closeImpersonationSession();

      const originalToken = getOriginalTokenForInfiltration();

      if (originalToken) {
        localStorage.setItem("usr_token", originalToken);
        clearOriginalTokenForInfiltration();
      } else {
        const response = await loginAsAdmin({
          clientId: accountInfo.superAdmin.clientId,
          userId: accountInfo.superAdmin.userId,
          reasonCategory: "other",
          reason: "Returning to own admin account after impersonation",
        });

        localStorage.setItem("usr_token", response.token);
      }

      localStorage.removeItem("pos-businessPremiseId");
      localStorage.removeItem("pos-electronicDeviceId");
      await deleteCookie({ cookieName: "pos-token" });

      window.location.reload();
    } catch (e) {
      if (isMfaReauthTriggeredError(e)) {
        return;
      }

      // We could not return to the admin account (e.g. the original admin token
      // is gone and the server cannot mint one from an impersonation token).
      // Rather than leave the user stuck in the impersonated session with a
      // dead-end error, sign them out so they can log back in as themselves.
      sentryLog.error("Failed to stop impersonation; logging out", {
        clientId: accountInfo.clientId,
        error: e instanceof Error ? e.message : String(e),
      });
      logout();
    }
  };

  const isIncognito = Boolean(accountInfo.superAdmin);

  return (
    <>
      <Popover isOpen={isUserPopoverOpen} onOpenChange={setIsUserPopoverOpen}>
        <Popover.Trigger>
          <div
            className={"-ml-0.5 flex cursor-pointer items-center gap-2"}
            data-identifier="user-popover-trigger"
          >
            <Avatar
              className="shrink-0 justify-start text-gray-500"
              size="md"
              src={accountInfo.userAvatar}
              name={`${accountInfo.userName} ${accountInfo.userLastName}`}
            ></Avatar>
            <div
              className={cn(
                "pointer-events-none opacity-0 transition-all duration-200 ease-out",
                {
                  "pointer-events-auto translate-x-0 opacity-100": sidebarOpen,
                },
              )}
            >
              <div className={"text-sm leading-4 font-medium text-gray-500"}>
                {accountInfo.userName} {accountInfo.userLastName}
              </div>
              <div className="text-xs leading-3 text-gray-400">
                {accountInfo.userEmail}
              </div>
            </div>
          </div>
        </Popover.Trigger>

        <Popover.Content
          className="bg-background border-divider w-[288px] border p-0 shadow-xl"
          placement="top start"
        >
          <Popover.Dialog className="p-0">
            <Popover.Arrow />
            <div className="flex w-full flex-col">
              <div className="flex items-start gap-3 p-4 pb-3">
                <Avatar
                  className="shrink-0"
                  size="md"
                  src={accountInfo.userAvatar}
                  name={`${accountInfo.userName} ${accountInfo.userLastName}`}
                />
                <div className="min-w-0 flex-1">
                  <p
                    className={cn(
                      "truncate text-sm font-semibold",
                      isIncognito ? "text-danger-700" : "text-foreground-800",
                    )}
                  >
                    {accountInfo.userName} {accountInfo.userLastName}
                  </p>
                  {accountInfo.userEmail ? (
                    <p
                      className={cn(
                        "truncate text-xs",
                        isIncognito ? "text-danger-500" : "text-foreground-500",
                      )}
                    >
                      {accountInfo.userEmail}
                    </p>
                  ) : null}
                  <p className="text-foreground-600 mt-1.5 truncate text-xs font-medium">
                    {accountInfo.businessName}
                  </p>
                  {accountInfo.superAdmin ? (
                    <CopyButton
                      text={accountInfo.clientId}
                      size="sm"
                      variant="secondary"
                      className="mt-1.5"
                    >
                      {accountInfo.clientId}
                    </CopyButton>
                  ) : null}
                </div>
              </div>

              {!isIncognito && isAdminUser ? (
                <div className="px-4 pb-3">
                  {isMfaStatusLoading && (
                    <div className="bg-default-100 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1">
                      <ShieldCheck
                        size={13}
                        className="text-default-500 shrink-0"
                      />
                      <span className="text-default-600 text-xs font-medium">
                        <Trans>Checking MFA status</Trans>
                      </span>
                    </div>
                  )}

                  {isMfaStatusError && (
                    <div className="bg-warning-50 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1">
                      <AlertTriangle
                        size={13}
                        className="text-warning shrink-0"
                      />
                      <span className="text-warning-700 text-xs font-medium">
                        <Trans>MFA status unavailable</Trans>
                      </span>
                    </div>
                  )}

                  {mfaStatus?.mfaEnabled && !isMfaStatusError && (
                    <div className="bg-success-50 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1">
                      <ShieldCheck
                        size={13}
                        className="text-success shrink-0"
                      />
                      <span className="text-success-700 text-xs font-medium">
                        <Trans>MFA enabled</Trans>
                      </span>
                    </div>
                  )}

                  {!mfaStatus?.mfaEnabled &&
                    mfaStatus?.hasPasskey &&
                    !isMfaStatusError && (
                      <div className="bg-default-100 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1">
                        <KeyRound
                          size={13}
                          className="text-default-500 shrink-0"
                        />
                        <span className="text-default-600 text-xs font-medium">
                          <Trans>Passkey sign-in on</Trans>
                        </span>
                      </div>
                    )}
                </div>
              ) : null}

              {isIncognito && accountInfo.superAdmin ? (
                <div className="px-4 pb-3">
                  <div className="bg-danger-50 rounded-lg px-3 py-2">
                    <p className="text-danger-600 text-[10px] font-semibold tracking-wide uppercase">
                      <Trans>Vaš račun</Trans>
                    </p>
                    <p className="text-danger-800 truncate text-xs font-medium">
                      {[
                        accountInfo.superAdmin.userName,
                        accountInfo.superAdmin.userLastName,
                      ]
                        .filter(Boolean)
                        .join(" ") || `User ${accountInfo.superAdmin.userId}`}
                    </p>
                    <p className="text-danger-600 truncate text-xs">
                      {accountInfo.superAdmin.businessName}
                    </p>
                    {accountInfo.superAdmin.userEmail ? (
                      <p className="text-danger-500 truncate text-xs">
                        {accountInfo.superAdmin.userEmail}
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}

              <Separator />

              <div className="flex flex-col gap-0.5 p-2">
                <AiChatToggle onOpened={() => setIsUserPopoverOpen(false)} />

                <SupportPopover
                  onListboxAction={(key) => {
                    if (key === "knowledgeBase") {
                      setIsKnowledgeBaseModalOpen(true);
                      setIsUserPopoverOpen(false);
                    }
                  }}
                  trigger={
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-foreground-700 h-9 w-full justify-start px-2"
                    >
                      <Headset size={16} className="shrink-0" />
                      <Trans>Podpora</Trans>
                    </Button>
                  }
                />

                {accountInfo.superAdmin ||
                accountInfo.systemRole === "admin" ? (
                  <ThemeSwitcher />
                ) : null}

                {isAdminUser && isMfaStatusError && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-warning-700 h-9 w-full justify-start px-2"
                    onPress={() => {
                      void refetchMfaStatus();
                    }}
                  >
                    <AlertTriangle size={16} className="shrink-0" />
                    {mfaStatusError instanceof Error
                      ? mfaStatusError.message
                      : undefined}
                    {t`Retry MFA status check`}
                  </Button>
                )}

                {!isIncognito && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-foreground-700 h-9 w-full justify-start px-2"
                    onPress={() => {
                      setIsUserPopoverOpen(false);
                      setIsPasskeysModalOpen(true);
                    }}
                  >
                    <KeyRound size={16} className="shrink-0" />
                    <Trans>Passkeys</Trans>
                  </Button>
                )}

                {isAdminUser && mfaStatus?.mfaEnabled && !isMfaStatusError && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-foreground-700 h-9 w-full justify-start px-2"
                    onPress={() => {
                      setIsUserPopoverOpen(false);
                      setIsMfaResetModalOpen(true);
                    }}
                  >
                    <ShieldOff size={16} className="shrink-0" />
                    <Trans>Reset MFA</Trans>
                  </Button>
                )}

                <Button
                  data-identifier="privacy-settings-button"
                  variant="ghost"
                  size="sm"
                  className="text-foreground-700 h-9 w-full justify-start px-2"
                  onPress={() => {
                    setIsUserPopoverOpen(false);
                    openPreferences();
                  }}
                >
                  <Cookie size={16} className="shrink-0" />
                  <Trans>Nastavitve zasebnosti</Trans>
                </Button>
              </div>

              <Separator />

              <div className="p-2">
                <Button
                  data-identifier="logout-button"
                  variant={isIncognito ? "danger-soft" : "ghost"}
                  size="sm"
                  className={cn(
                    "h-9 w-full justify-start px-2",
                    isIncognito
                      ? "text-danger-700"
                      : "text-foreground-700 data-[hover=true]:text-danger",
                  )}
                  onPress={isIncognito ? handleLoginAsAdmin : logout}
                >
                  {isIncognito ? (
                    <>
                      <Drama size={16} className="shrink-0" />
                      <Trans>Nazaj v admin račun</Trans>
                    </>
                  ) : (
                    <>
                      <LogOut size={16} className="shrink-0" />
                      <Trans>Odjava</Trans>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Popover.Dialog>
        </Popover.Content>
      </Popover>

      <KnowledgeBaseModal
        isOpen={isKnowledgeBaseModalOpen}
        onClose={() => setIsKnowledgeBaseModalOpen(false)}
      />

      <MfaResetModal
        isOpen={isMfaResetModalOpen}
        onClose={() => setIsMfaResetModalOpen(false)}
        onSuccess={logout}
      />

      <PasskeysModal
        isOpen={isPasskeysModalOpen}
        onClose={() => setIsPasskeysModalOpen(false)}
      />
    </>
  );
};

const SupportPopover = ({
  trigger,
  onListboxAction,
}: {
  trigger: React.ReactNode;
  onListboxAction?: (key: Key) => void;
}) => {
  const [isSupportPopoverOpen, setIsSupportPopoverOpen] = useState(false);

  const items = [
    {
      country: "Slovenija",
      phone: "+386 40 234 606",
      email: "support@lime-booking.com",
      knowledgeBaseUrl: "https://lime-booking.productfruits.help/sl",
    },
    {
      country: "Hrvatska",
      phone: "+385 97 6727 477",
      email: "support@lime-booking.com",
    },
    {
      country: "Srbija",
      phone: "+381 66 5626 392",
      email: "support@lime-booking.com",
    },
  ];

  return (
    <Popover
      isOpen={isSupportPopoverOpen}
      onOpenChange={setIsSupportPopoverOpen}
    >
      <Popover.Trigger>{trigger}</Popover.Trigger>
      <Popover.Content placement="top start">
        <Popover.Dialog className="p-0">
          <Popover.Arrow />
          <div className="min-w-65 px-2 pt-2 pb-2">
            {items.map((item, index) => (
              <Fragment key={item.country}>
                {index > 0 ? <Separator /> : null}
                <p className="px-2 py-1.5 text-xs font-semibold text-foreground-500">
                  {item.country}
                </p>
                <div className="flex flex-col gap-1.5 mb-2">
                  {item.knowledgeBaseUrl ? (
                    <ButtonGroup fullWidth size="sm" variant="secondary">
                      <Button
                        className="flex-1 justify-start text-foreground-700 py-5"
                        onPress={() => {
                          setIsSupportPopoverOpen(false);
                          onListboxAction?.("knowledgeBase");
                        }}
                      >
                        <BookOpenCheck size={16} className="shrink-0" />
                        <Trans>Baza znanja</Trans>
                      </Button>
                      <Button
                        aria-label={t`Odpri bazo znanja`}
                        isIconOnly
                        className="py-5"
                        onPress={() =>
                          window.open(
                            item.knowledgeBaseUrl,
                            "_blank",
                            "noreferrer",
                          )
                        }
                      >
                        <ButtonGroup.Separator />
                        <ExternalLink size={16} className="shrink-0" />
                      </Button>
                    </ButtonGroup>
                  ) : null}
                  <ButtonGroup fullWidth size="sm" variant="secondary">
                    <Button
                      className="flex-1 justify-start text-foreground-700 py-5"
                      onPress={() => {
                        window.location.href = `tel:${item.phone}`;
                      }}
                    >
                      <Phone size={16} className="shrink-0" />
                      <div className="flex flex-col items-start text-left">
                        <span>
                          <Trans>Telefon</Trans>
                        </span>
                        <span className="text-foreground-400 text-xs font-normal">
                          {item.phone}
                        </span>
                      </div>
                    </Button>
                    <CopyButton
                      size="sm"
                      text={item.phone}
                      variant="secondary"
                      withButtonGroupSeparator
                      className="py-5"
                    />
                  </ButtonGroup>
                  <ButtonGroup fullWidth size="sm" variant="secondary">
                    <Button
                      className="flex-1 justify-start text-foreground-700 py-5"
                      onPress={() => {
                        window.location.href = `mailto:${item.email}`;
                      }}
                    >
                      <Mail size={16} className="shrink-0" />
                      <div className="flex flex-col items-start text-left">
                        <span>
                          <Trans>Email</Trans>
                        </span>
                        <span className="text-foreground-400 text-xs font-normal">
                          {item.email}
                        </span>
                      </div>
                    </Button>
                    <CopyButton
                      size="sm"
                      text={item.email}
                      variant="secondary"
                      withButtonGroupSeparator
                      className="py-5"
                    />
                  </ButtonGroup>
                </div>
              </Fragment>
            ))}
          </div>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
};

const THEME_OPTIONS = [
  { icon: Sun, id: "light", label: () => t`Svetla` },
  { icon: Moon, id: "dark", label: () => t`Temna` },
  { icon: Monitor, id: "system", label: () => t`Sistem` },
] as const;

const ThemeSwitcher = () => {
  const { theme, setTheme } = useTheme();
  const active =
    THEME_OPTIONS.find((o) => o.id === (theme ?? "system")) ?? THEME_OPTIONS[2];
  const ActiveIcon = active.icon;

  return (
    <div className="flex h-9 w-full items-center gap-1 px-2">
      <span key={active.id} className="theme-icon-in shrink-0">
        <ActiveIcon size={16} className="text-foreground-500" />
      </span>
      <span className="text-foreground-700 flex-1 text-xs font-medium">
        <Trans>Tema</Trans>
      </span>
      <ToggleButtonGroup
        aria-label={t`Izberi temo`}
        disallowEmptySelection
        selectedKeys={new Set([theme ?? "system"])}
        selectionMode="single"
        size="sm"
        onSelectionChange={(keys) => {
          const next = [...keys][0];
          if (next == null) return;
          setTheme(String(next));
        }}
      >
        {THEME_OPTIONS.map((option, index) => (
          <ToggleButton
            key={option.id}
            aria-label={option.label()}
            className="px-3 py-2"
            id={option.id}
            isIconOnly
          >
            {index > 0 ? <ToggleButtonGroup.Separator /> : null}
            <option.icon size={15} />
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </div>
  );
};
